import { getAssignmentAppsOptions } from "@/lib/data/react-query/matters/matter-task";
import MatterTask from "@/components/features/matter/tasks/MatterTask";
import { MatterTasksBoard } from "@/components/features/matter/tasks/MatterTasksBoard";
import {
  TaskQuickFilters,
  type TaskSortOption,
} from "@/components/features/matter/tasks/TaskQuickFilters";
import {
  ActionEmptyState,
  Badge,
  Button,
  Input,
  LoadingSpinner,
  TaskStatusIcon,
  Text,
} from "@/components/ui";
import { type FilterOption } from "@/components/ui/FilterSelector/FilterSelector";
import type { MatterBreadcrumbProps } from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import { type ViewMode } from "@/components/ui/ViewModeToggle/ViewModeToggle";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import {
  useTasksViewModel,
  type MatterTaskWithRoles,
} from "@/hooks/matter-view-models/use-tasks-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { getUsersOptions } from "@/lib/data/react-query";
import {
  useInfiniteMatterTasks,
  useUpdateMatterTask,
} from "@/lib/data/react-query/matters/matter-task";
import { useMassTortTasks } from "@/hooks/use-mass-tort-tasks";
import { type RetrieveMatterTasksParams } from "@/lib/data/requests/matters/matter-task";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type DirectoryUser,
  type Matter,
  type MatterSubItemType,
  type MatterTask as MatterTaskType,
} from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import Fuse from "fuse.js";
import { LockKeyhole, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type MatterTaskWithType } from "./matter-task-columns";

const taskParentIdFieldBySubItemType: Partial<
  Record<MatterSubItemType, keyof MatterTaskWithRoles>
> = {
  "phase-action": "phaseActionId",
  file: "fileId",
  vehicle: "vehicleId",
  "incident-report": "incidentReportId",
  insurance: "insuranceId",
  "medical-treatment": "medicalTreatmentId",
  "medical-treatment-bill": "medicalTreatmentBillId",
  "medical-treatment-appointment": "medicalTreatmentAppointmentId",
  "medical-treatment-plan": "medicalTreatmentPlanId",
  "medical-treatment-record": "medicalTreatmentRecordId",
  "medical-treatment-balance-confirmation":
    "medicalTreatmentBalanceConfirmationId",
  "settlement-demand": "settlementDemandId",
  "settlement-offer": "settlementOfferId",
  "settlement-counter": "settlementCounterId",
  expense: "expenseId",
  "lost-wage": "lostWagesId",
  lien: "lienId",
  "property-damage": "propertyDamageId",
  "signature-request": "signatureRequestId",
  "trust-check": "trustCheckId",
  "matter-litigation-date": "litigationDateId",
  "statute-of-limitations": "statuteOfLimitationsId",
  email: "emailId",
};
const UNASSIGNED_ASSIGNEE_FILTER_VALUE = "__unassigned_assignee__";
const UNASSIGNED_ASSIGNER_FILTER_VALUE = "__unassigned_assigner__";
const NO_PRIORITY_FILTER_VALUE = "__no_priority__";
// Temporary flag to re-enable drag & drop once UX is ready again.
const BOARD_DRAG_AND_DROP_ENABLED: boolean = false;
const TASK_STATUS_GROUPS = [
  {
    status: "In Progress",
    value: "in-progress",
    label: "In progress",
    iconStatus: "in-progress",
  },
  { status: "Open", value: "open", label: "Open", iconStatus: "open" },
  {
    status: "Completed",
    value: "done",
    label: "Completed",
    iconStatus: "completed",
  },
] as const;

type TaskSortOptionValue = TaskSortOption;

export type MatterTasksFilters = Omit<
  RetrieveMatterTasksParams,
  "matterId" | "pageNumber" | "pageSize" | "status"
>;

type ProvidedTaskGroupPagination = Partial<
  Record<
    (typeof TASK_STATUS_GROUPS)[number]["status"],
    {
      fetchNextPage: () => void;
      hasNextPage: boolean;
      isFetchingNextPage: boolean;
      totalCount: number;
    }
  >
>;

export default function MatterTasks({
  tasks: providedTasks,
  matter,
  massTortId,
  subItem,
  taskId,
  shrinkCreateButton = false,
  defaultVariant = "card",
  showAnimation = false,
  showCreateButton = false,
  showFilters = true,
  showQuickFilters = true,
  loadMoreMode = "infinite",
  onTaskCountChange,
  getCustomBreadcrumbItems,
  providedTaskGroupPagination,
  onProvidedTaskFiltersChange,
  showAssigneeFilter = true,
}: {
  tasks: MatterTaskWithRoles[];
  matter: Matter;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
  taskId?: string;
  shrinkCreateButton?: boolean;
  defaultVariant?: "card" | "eventlog" | "list";
  showAnimation?: boolean;
  /** Retained for caller compatibility; the streamlined toolbar has no view toggle. */
  showViewToggle?: boolean;
  /** Retained for caller compatibility; task selection is now row-level only. */
  showSelectTools?: boolean;
  showCreateButton?: boolean;
  showFilters?: boolean;
  showQuickFilters?: boolean;
  /** Retained for caller compatibility; filters use the quick-chip layout. */
  filterSelectorFullWidth?: boolean;
  bareLayout?: boolean;
  loadMoreMode?: "infinite" | "button" | "all";
  onTaskCountChange?: (count: number | undefined) => void;
  getCustomBreadcrumbItems?: (
    task: MatterTaskWithType,
  ) => MatterBreadcrumbProps["items"] | undefined;
  providedTaskGroupPagination?: ProvidedTaskGroupPagination;
  onProvidedTaskFiltersChange?: (filters: MatterTasksFilters) => void;
  showAssigneeFilter?: boolean;
}) {
  const { hasPagePermission, hasActionPermission, currentUser } = useUser();
  const { openTaskModal } = useGlobalModal();
  const navigate = useNavigate();
  const hasViewPermission = hasPagePermission(
    massTortId
      ? PAGE_PERMISSION_KEYS.massTortsDetailTasks
      : PAGE_PERMISSION_KEYS.mattersDetailTasks,
  );
  const hasCreatePermission = hasActionPermission(
    massTortId
      ? ACTION_PERMISSION_KEYS.massTort
      : ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasUpdatePermission = hasActionPermission(
    massTortId
      ? ACTION_PERMISSION_KEYS.massTort
      : ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasUsersPermission = Boolean(true);
  const updateMatterTask = useUpdateMatterTask();
  const shouldFetchMatterTasks = !massTortId && loadMoreMode !== "all";
  const usesServerFilters =
    shouldFetchMatterTasks || Boolean(onProvidedTaskFiltersChange);
  const [query, setQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<
    Partial<Record<string, string[]>>
  >({});
  const [draftTaskFilters, setDraftTaskFilters] = useState<
    Partial<Record<string, string[]>>
  >({});
  const assigneeDirectory = useUserDirectory({
    enabled: hasUsersPermission && showAssigneeFilter,
    selectedIds: [
      ...(selectedFilters.assigneeId ?? []),
      ...(draftTaskFilters.assigneeId ?? []),
    ].filter(
      (id) => id !== UNASSIGNED_ASSIGNEE_FILTER_VALUE && !id.startsWith("app:"),
    ),
  });
  const assignerDirectory = useUserDirectory({
    enabled: hasUsersPermission,
    selectedIds: [
      ...(selectedFilters.assignerId ?? []),
      ...(draftTaskFilters.assignerId ?? []),
    ].filter((id) => id !== UNASSIGNED_ASSIGNER_FILTER_VALUE),
  });
  const [sortOption, setSortOption] =
    useState<TaskSortOptionValue>("createdDateDesc");
  const { data: assignmentApps = [] } = useQuery(
    getAssignmentAppsOptions(!massTortId),
  );
  const matterTaskFilters = useMemo<MatterTasksFilters>(() => {
    const assigneeIds = selectedFilters.assigneeId ?? [];
    const assignerIds = selectedFilters.assignerId ?? [];
    const priority = (selectedFilters.priority ?? []).filter(
      (
        value,
      ): value is NonNullable<RetrieveMatterTasksParams["priority"]>[number] =>
        value !== NO_PRIORITY_FILTER_VALUE,
    );
    const order = sortOption.endsWith("Asc") ? "asc" : "desc";
    const sort = sortOption.startsWith("createdDate")
      ? "dateCreated"
      : sortOption.startsWith("dueDate")
        ? "dueDate"
        : "priority";

    return {
      assigneeUserId: assigneeIds.filter(
        (id) =>
          id !== UNASSIGNED_ASSIGNEE_FILTER_VALUE && !id.startsWith("app:"),
      ),
      assigneeIntegrationAppId: assigneeIds
        .filter((id) => id.startsWith("app:"))
        .map((id) => id.slice(4)),
      assignerUserId: assignerIds.filter(
        (id) => id !== UNASSIGNED_ASSIGNER_FILTER_VALUE,
      ),
      createdDateFrom: selectedFilters.createdDateAfter?.[0],
      createdDateTo: selectedFilters.createdDateBefore?.[0],
      dueDateFrom: selectedFilters.dueDateAfter?.[0],
      dueDateTo: selectedFilters.dueDateBefore?.[0],
      order,
      priority,
      priorityUnassigned: (selectedFilters.priority ?? []).includes(
        NO_PRIORITY_FILTER_VALUE,
      ),
      search: query.trim() || undefined,
      sort,
      timeframe:
        selectedFilters.timeframe as RetrieveMatterTasksParams["timeframe"],
      unassigned: assigneeIds.includes(UNASSIGNED_ASSIGNEE_FILTER_VALUE),
    };
  }, [query, selectedFilters, sortOption]);
  const taskQueryInput = {
    matterId: shouldFetchMatterTasks ? matter.id : "",
    pageSize: 50,
    ...matterTaskFilters,
    phaseActionId: subItem?.type === "phase-action" ? subItem.id : undefined,
  };
  const inProgressTasksQuery = useInfiniteMatterTasks({
    ...taskQueryInput,
    status: "In Progress",
  });
  const openTasksQuery = useInfiniteMatterTasks({
    ...taskQueryInput,
    status: "Open",
  });
  const completedTasksQuery = useInfiniteMatterTasks({
    ...taskQueryInput,
    status: "Completed",
  });
  const tasksQueryByStatus = useMemo(
    () => ({
      "In Progress": inProgressTasksQuery,
      Open: openTasksQuery,
      Completed: completedTasksQuery,
    }),
    [completedTasksQuery, inProgressTasksQuery, openTasksQuery],
  );
  const taskResponses = Object.values(tasksQueryByStatus);
  const { query: massTortTasksQuery, tasks: paginatedMassTortTasks } =
    useMassTortTasks({
      massTortId,
      matter,
      enabled: hasViewPermission,
      sortOption,
      loadAll: Boolean(
        query.trim() ||
          subItem ||
          sortOption !== "createdDateDesc" ||
          Object.values(selectedFilters).some((values) => values?.length),
      ),
    });
  const isLoadingTasks = massTortId
    ? massTortTasksQuery.isLoading
    : taskResponses.some((taskQuery) => taskQuery.isLoading);
  const taskCount = massTortId
    ? massTortTasksQuery.data?.pages[0]?.pagination?.count
    : shouldFetchMatterTasks && !isLoadingTasks
      ? taskResponses.reduce(
          (count, taskQuery) =>
            count + (taskQuery.data?.pages[0]?.pagination?.count ?? 0),
          0,
        )
      : undefined;
  const { allTasks: paginatedTasks } = useTasksViewModel(
    matter,
    taskResponses.flatMap(
      (taskQuery) => taskQuery.data?.pages.flatMap((page) => page.data) ?? [],
    ),
  );
  const tasks = massTortId
    ? paginatedMassTortTasks
    : shouldFetchMatterTasks
      ? paginatedTasks
      : providedTasks;
  const {
    fetchNextPage: fetchMoreMassTortTasks,
    hasNextPage: hasMoreMassTortTasks,
    isFetching: isFetchingMassTortTasks,
    isError: isMassTortTasksError,
  } = massTortTasksQuery;
  useEffect(() => {
    if (!shouldFetchMatterTasks) {
      onProvidedTaskFiltersChange?.(matterTaskFilters);
    }
  }, [matterTaskFilters, onProvidedTaskFiltersChange, shouldFetchMatterTasks]);
  useEffect(() => {
    onTaskCountChange?.(taskCount);
  }, [onTaskCountChange, taskCount]);
  const { data: usersData } = useQuery(getUsersOptions());
  const users = useMemo(() => usersData?.data ?? [], [usersData?.data]);
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );

  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [viewMode] = useState<ViewMode>(defaultVariant);
  const [taskStatusOverrides, setTaskStatusOverrides] = useState<
    Record<string, MatterTaskType["status"]>
  >({});
  // Global slideover management
  const { handleTaskSearch } = useHandleSearchParams();
  const processedSearches = useRef<Set<string>>(new Set());

  const handleAddFilter = (key: string, value: string) => {
    if (!value) {
      return;
    }

    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const singleValueKeys = new Set([
        "dueDateBefore",
        "dueDateAfter",
        "createdDateBefore",
        "createdDateAfter",
      ]);

      if (!singleValueKeys.has(key) && existingValues.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        [key]: singleValueKeys.has(key) ? [value] : [...existingValues, value],
      };
    });
  };

  const handleRemoveFilter = (key: string, value: string) => {
    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const nextValues = existingValues.filter((current) => current !== value);

      if (nextValues.length === 0) {
        const { [key]: removed, ...rest } = prev;
        void removed;
        return rest;
      }

      return {
        ...prev,
        [key]: nextValues,
      };
    });
  };

  // Handle URL-based task search using the global hook
  useEffect(() => {
    if (!taskId) return;

    const searchKey = `task-${taskId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    if (massTortId) {
      openTaskModal({
        matterId: matter.id,
        massTortId,
        taskId,
        editing: true,
        initialTab: "details",
      });

      const url = new URL(window.location.href);
      url.searchParams.delete("taskId");
      void navigate({
        to: url.pathname + url.search,
        replace: true,
      });
      return;
    }

    handleTaskSearch(matter, taskId);
  }, [
    taskId,
    matter,
    massTortId,
    handleTaskSearch,
    navigate,
    openTaskModal,
    shouldFetchMatterTasks,
  ]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    if (hasUsersPermission || assignmentApps.length) {
      const getUserItems = (
        directoryUsers: Pick<DirectoryUser, "id" | "firstName" | "lastName">[],
      ) =>
        Array.from(
          new Map(
            [currentUser, ...directoryUsers].map((user) => [user.id, user]),
          ).entries(),
        )
          .sort(([, userA], [, userB]) =>
            (formatUsersName(userA) ?? "").localeCompare(
              formatUsersName(userB) ?? "",
            ),
          )
          .reduce<{ label: string; value: string }[]>((items, [id, user]) => {
            const label = formatUsersName(user) ?? id;
            const item = {
              label: id === currentUser.id ? `${label} (me)` : label,
              value: id,
            };

            if (id === currentUser.id) {
              items.unshift(item);
            } else {
              items.push(item);
            }
            return items;
          }, []);
      const assigneeItems = [
        ...assignmentApps.map((app) => ({
          label: `${app.integrationAppName ?? "App"} (app)`,
          value: `app:${app.integrationAppId}`,
          group: "Installed apps",
        })),
        ...getUserItems([
          ...matter.assignees
            .filter((assignee) => !assignee.dateTimeEnd)
            .map((assignee) => assignee.user),
          ...assigneeDirectory.users,
        ])
          .map((item) => {
            const isAssigned = matter.assignees.some(
              (assignee) =>
                !assignee.dateTimeEnd && assignee.user.id === item.value,
            );
            const roles = Array.from(
              new Set(
                matter.assignees
                  .filter(
                    (assignee) =>
                      !assignee.dateTimeEnd && assignee.user.id === item.value,
                  )
                  .map((assignee) => assignee.role.label)
                  .filter(Boolean),
              ),
            );
            return {
              ...item,
              group: isAssigned ? "Assigned to this matter" : "All users",
              searchText: roles.join(" "),
              displayLabel: roles.length ? (
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                  <span>{item.label}</span>
                  {roles.map((role) => (
                    <Badge key={role}>{role}</Badge>
                  ))}
                </div>
              ) : undefined,
            };
          })
          .sort(
            (a, b) =>
              Number(b.group === "Assigned to this matter") -
              Number(a.group === "Assigned to this matter"),
          ),
        {
          label: "Unassigned",
          value: UNASSIGNED_ASSIGNEE_FILTER_VALUE,
          group: "All users",
        },
      ];
      const assignerItems = [
        ...getUserItems(assignerDirectory.users),
        { label: "Unassigned", value: UNASSIGNED_ASSIGNER_FILTER_VALUE },
      ];

      if (showAssigneeFilter && assigneeItems.length) {
        options.push({
          label: "Assignee",
          value: "assigneeId",
          input: {
            ...assigneeDirectory.selectProps,
            type: "searchable-select",
            placeholder: "Select an assignee",
            items: assigneeItems,
          },
        });
      }

      if (assignerItems.length) {
        options.push({
          label: "Assigner",
          value: "assignerId",
          input: {
            ...assignerDirectory.selectProps,
            type: "searchable-select",
            placeholder: "Select an assigner",
            items: assignerItems,
          },
        });
      }
    }

    options.push({
      label: "Due date",
      value: "dueDate",
      input: {
        type: "date",
        placeholder: "Select due date",
        conditionOptions: [
          { label: "Before", value: "dueDateBefore" },
          { label: "After", value: "dueDateAfter" },
        ],
      },
    });

    options.push({
      label: "Created",
      value: "createdDate",
      input: {
        type: "date",
        placeholder: "Select created date",
        conditionOptions: [
          { label: "Before", value: "createdDateBefore" },
          { label: "After", value: "createdDateAfter" },
        ],
      },
    });

    options.push({
      label: "Timeframe",
      value: "timeframe",
      input: {
        type: "select",
        placeholder: "Select timeframe",
        items: [
          { label: "Overdue", value: "overdue" },
          { label: "Today", value: "today" },
          { label: "This week", value: "thisWeek" },
          { label: "Next week", value: "nextWeek" },
          { label: "Next 30 days", value: "next30Days" },
        ],
      },
    });

    options.push({
      label: "Priority",
      value: "priority",
      input: {
        type: "select",
        placeholder: "Select priority",
        items: [
          ...MATTER_TASK_PRIORITY_OPTIONS.map((priority) => ({
            label: priority,
            value: priority,
          })),
          { label: "No priority", value: NO_PRIORITY_FILTER_VALUE },
        ],
      },
    });

    return options;
  }, [
    hasUsersPermission,
    assignmentApps,
    currentUser,
    showAssigneeFilter,
    matter.assignees,
    assigneeDirectory.users,
    assigneeDirectory.selectProps,
    assignerDirectory.users,
    assignerDirectory.selectProps,
  ]);

  const tasksWithType = useMemo(
    () =>
      tasks
        .filter((task) => {
          if (!subItem) {
            return true;
          }

          const parentIdField = taskParentIdFieldBySubItemType[subItem.type];
          return parentIdField ? task[parentIdField] === subItem.id : false;
        })
        .map((task) => {
          let type = "Matter";
          let itemId = matter.id;
          if (task.phaseActionId) {
            type = "Phase action";
            itemId = task.phaseActionId;
          }
          if (task.vehicleId) {
            type = "Vehicle";
            itemId = task.vehicleId;
          }
          if (task.incidentReportId) {
            type = "Incident Report";
            itemId = task.incidentReportId;
          }
          if (task.insuranceId) {
            type = "Insurance";
            itemId = task.insuranceId;
          }
          if (task.medicalTreatmentId) {
            type = "Medical Treatment";
            itemId = task.medicalTreatmentId;
          }
          if (task.medicalTreatmentAppointmentId) {
            type = "Medical Treatment Date";
            itemId = task.medicalTreatmentAppointmentId;
          }

          if (task.medicalTreatmentBillId) {
            type = "Medical Treatment Bill";
            itemId = task.medicalTreatmentBillId;
          }

          if (task.medicalTreatmentRecordId) {
            type = "Medical Treatment Record";
            itemId = task.medicalTreatmentRecordId;
          }
          if (task.medicalTreatmentPlanId) {
            type = "Treatment Plan";
            itemId = task.medicalTreatmentPlanId;
          }
          if (task.medicalTreatmentBalanceConfirmationId) {
            type = "Balance Confirmation";
            itemId = task.medicalTreatmentBalanceConfirmationId;
          }
          if (task.settlementDemandId) {
            type = "Demand";
            itemId = task.settlementDemandId;
          }
          if (task.settlementOfferId) {
            type = "Offer";
            itemId = task.settlementOfferId;
          }
          if (task.settlementCounterId) {
            type = "Counter";
            itemId = task.settlementCounterId;
          }
          if (task.expenseId) {
            type = "Cost";
            itemId = task.expenseId;
          }
          if (task.lostWagesId) {
            type = "Lost Wage";
            itemId = task.lostWagesId;
          }
          if (task.lienId) {
            type = "Lien";
            itemId = task.lienId;
          }
          if (task.propertyDamageId) {
            type = "Property Damage";
            itemId = task.propertyDamageId;
          }

          if (task.trustCheckId) {
            type = "Trust Check";
            itemId = task.trustCheckId;
          }

          if (task.litigationDateId) {
            type = "Litigation Date";
            itemId = task.litigationDateId;
          }
          if (task.signatureRequestId) {
            type = "Signature Request";
            itemId = task.signatureRequestId;
          }
          if (task.statuteOfLimitationsId) {
            type = "Statute of Limitations";
            itemId = task.statuteOfLimitationsId;
          }
          if (task.emailId) {
            type = "Email";
            itemId = task.emailId;
          }
          if (task.fileId) {
            type = "File";
            itemId = task.fileId;
          }

          return {
            ...task,
            type,
            itemId,
          };
        }),
    [tasks, matter.id, subItem],
  );

  useEffect(() => {
    setTaskStatusOverrides((previous) => {
      const entries = Object.entries(previous);
      if (!entries.length) {
        return previous;
      }

      const statusMap = new Map(
        tasksWithType.map((task) => [task.id, task.status]),
      );

      const filteredEntries = entries.filter(([taskId, status]) => {
        const latestStatus = statusMap.get(taskId);
        return latestStatus !== undefined && latestStatus !== status;
      });

      if (filteredEntries.length === entries.length) {
        return previous;
      }

      return filteredEntries.reduce<Record<string, MatterTaskType["status"]>>(
        (acc, [taskId, status]) => {
          acc[taskId] = status;
          return acc;
        },
        {},
      );
    });
  }, [tasksWithType]);

  const tasksWithOverrides = useMemo(
    () =>
      tasksWithType.map((task) => {
        const override = taskStatusOverrides[task.id];
        if (!override) {
          return task;
        }

        return {
          ...task,
          status: override,
        };
      }),
    [taskStatusOverrides, tasksWithType],
  );

  const filteredByFilters = useMemo(() => {
    if (usesServerFilters) {
      return tasksWithOverrides;
    }

    const {
      assignerId: assignerIds = [],
      assigneeId: assigneeIds = [],
      timeframe: timeframeValues = [],
    } = selectedFilters;
    const priorityValues = selectedFilters.priority ?? [];
    const dueDateAfterValue = (selectedFilters.dueDateAfter ?? [])[0];
    const dueDateBeforeValue = (selectedFilters.dueDateBefore ?? [])[0];
    const createdDateAfterValue = (selectedFilters.createdDateAfter ?? [])[0];
    const createdDateBeforeValue = (selectedFilters.createdDateBefore ?? [])[0];

    const dueDateAfterBoundary = dueDateAfterValue
      ? dayjs(dueDateAfterValue, "YYYY-MM-DD", true).startOf("day")
      : null;
    const dueDateBeforeBoundary = dueDateBeforeValue
      ? dayjs(dueDateBeforeValue, "YYYY-MM-DD", true).endOf("day")
      : null;
    const createdDateAfterBoundary = createdDateAfterValue
      ? dayjs(createdDateAfterValue, "YYYY-MM-DD", true).startOf("day")
      : null;
    const createdDateBeforeBoundary = createdDateBeforeValue
      ? dayjs(createdDateBeforeValue, "YYYY-MM-DD", true).endOf("day")
      : null;

    return tasksWithOverrides.filter((task) => {
      const matchesAssigners = (() => {
        if (!assignerIds.length) {
          return true;
        }

        const includesUnassigned = assignerIds.includes(
          UNASSIGNED_ASSIGNER_FILTER_VALUE,
        );
        const filteredAssignerIds = assignerIds.filter(
          (id) => id !== UNASSIGNED_ASSIGNER_FILTER_VALUE,
        );
        const matchesAssignedUsers = filteredAssignerIds.some((id) =>
          task.assigners.some((assigner) => assigner.userId === id),
        );
        const hasNoAssigners =
          task.assigners.length === 0 ||
          task.assigners.every((assigner) => !assigner.userId);
        return matchesAssignedUsers || (includesUnassigned && hasNoAssigners);
      })();

      const matchesAssignees = (() => {
        if (!assigneeIds.length) {
          return true;
        }

        const includesUnassigned = assigneeIds.includes(
          UNASSIGNED_ASSIGNEE_FILTER_VALUE,
        );
        const filteredAssigneeIds = assigneeIds.filter(
          (id) => id !== UNASSIGNED_ASSIGNEE_FILTER_VALUE,
        );
        const matchesAssignedUsers = filteredAssigneeIds.some((id) =>
          task.assignees.some(
            (assignee) =>
              assignee.userId === id ||
              (assignee.integrationAppId &&
                `app:${assignee.integrationAppId}` === id),
          ),
        );
        const hasNoAssignees =
          task.assignees.length === 0 ||
          task.assignees.every(
            (assignee) => !assignee.userId && !assignee.integrationAppId,
          );
        return matchesAssignedUsers || (includesUnassigned && hasNoAssignees);
      })();

      const matchesDueDate = (() => {
        if (!dueDateAfterBoundary && !dueDateBeforeBoundary) {
          return true;
        }

        if (!task.dueDate) {
          return false;
        }

        const dueDate = dayjs(task.dueDate);
        if (!dueDate.isValid()) {
          return false;
        }

        if (dueDateAfterBoundary && dueDate.isBefore(dueDateAfterBoundary)) {
          return false;
        }

        if (dueDateBeforeBoundary && dueDate.isAfter(dueDateBeforeBoundary)) {
          return false;
        }

        return true;
      })();

      const matchesCreatedDate = (() => {
        if (!createdDateAfterBoundary && !createdDateBeforeBoundary) {
          return true;
        }

        if (!task.dateCreated) {
          return false;
        }

        const createdDate = dayjs(task.dateCreated);
        if (!createdDate.isValid()) {
          return false;
        }

        if (
          createdDateAfterBoundary &&
          createdDate.isBefore(createdDateAfterBoundary)
        ) {
          return false;
        }

        if (
          createdDateBeforeBoundary &&
          createdDate.isAfter(createdDateBeforeBoundary)
        ) {
          return false;
        }

        return true;
      })();

      const matchesTimeframe = (() => {
        if (!timeframeValues.length) {
          return true;
        }

        const currentDate = dayjs();
        if (!task.startDate) {
          return timeframeValues.includes("active");
        }
        const startDate = dayjs(task.startDate);
        if (!startDate.isValid()) {
          return timeframeValues.includes("active");
        }
        return (
          (timeframeValues.includes("upcoming") &&
            startDate.isAfter(currentDate)) ||
          (timeframeValues.includes("active") &&
            (startDate.isBefore(currentDate) ||
              startDate.isSame(currentDate, "day")))
        );
      })();

      const matchesPriority = (() => {
        if (!priorityValues.length) {
          return true;
        }

        const includesNoPriority = priorityValues.includes(
          NO_PRIORITY_FILTER_VALUE,
        );
        const selectedPriorityValues = priorityValues.filter(
          (value) => value !== NO_PRIORITY_FILTER_VALUE,
        );
        if (!task.priority) return includesNoPriority;
        return selectedPriorityValues.includes(task.priority);
      })();

      return (
        matchesAssigners &&
        matchesAssignees &&
        matchesDueDate &&
        matchesCreatedDate &&
        matchesTimeframe &&
        matchesPriority
      );
    });
  }, [selectedFilters, tasksWithOverrides, usesServerFilters]);

  const filteredTasks = useMemo(() => {
    if (usesServerFilters) {
      return filteredByFilters;
    }

    const sanitizedQuery = query.trim();
    if (!sanitizedQuery) {
      return filteredByFilters;
    }

    const searchable = filteredByFilters.map((task) => ({
      ...task,
      assignerNames: task.assigners
        .map((assigner) => formatUsersName(usersById.get(assigner.id)) ?? "")
        .join(" "),
      assigneeNames: task.assignees
        .map((assignee) =>
          assignee.integrationAppId
            ? (assignee.integrationAppName ?? "")
            : (formatUsersName(usersById.get(assignee.userId ?? "")) ?? ""),
        )
        .join(" "),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "description", weight: 0.5 },
        { name: "title", weight: 0.3 },
        { name: "assigneeNames", weight: 0.2 },
        { name: "assignerNames", weight: 0.2 },
        { name: "status", weight: 0.2 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [filteredByFilters, query, usesServerFilters, usersById]);

  const hasResetFilters = useRef(false);

  useEffect(() => {
    if (!hasResetFilters.current) {
      hasResetFilters.current = true;
      return;
    }

    return;
  }, [query, selectedFilters]);

  const taskGroups = useMemo(
    () =>
      TASK_STATUS_GROUPS.map((group) => {
        const taskQuery = tasksQueryByStatus[group.status];
        const providedPagination = providedTaskGroupPagination?.[group.status];
        return {
          ...group,
          tasks: filteredTasks.filter((task) => task.status === group.status),
          totalCount: shouldFetchMatterTasks
            ? (taskQuery.data?.pages[0]?.pagination?.count ?? 0)
            : (providedPagination?.totalCount ??
              filteredTasks.filter((task) => task.status === group.status)
                .length),
          fetchNextPage:
            providedPagination?.fetchNextPage ?? taskQuery.fetchNextPage,
          hasNextPage: providedPagination?.hasNextPage ?? taskQuery.hasNextPage,
          isFetchingNextPage:
            providedPagination?.isFetchingNextPage ??
            taskQuery.isFetchingNextPage,
        };
      }),
    [
      filteredTasks,
      providedTaskGroupPagination,
      shouldFetchMatterTasks,
      tasksQueryByStatus,
    ],
  );

  const selectedTasks = useMemo(() => {
    return filteredTasks.filter((task) => selectedTaskIds.has(task.id));
  }, [filteredTasks, selectedTaskIds]);

  const handleToggleTaskSelection = useCallback((taskId: string) => {
    setSelectedTaskIds((previous) => {
      const next = new Set(previous);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const handleBoardStatusChange = useCallback(
    async (task: MatterTaskWithType, nextStatus: MatterTaskType["status"]) => {
      if (task.status === nextStatus) {
        return;
      }

      setTaskStatusOverrides((previous) => ({
        ...previous,
        [task.id]: nextStatus,
      }));
      const targetMatterId = task.matterId;

      try {
        await updateMatterTask.mutateAsync({
          ...(massTortId ? { massTortId } : { matterId: targetMatterId }),
          id: task.id,
          status: nextStatus,
          dateModified: task.dateModified,
          completedDate:
            nextStatus === "Completed"
              ? task.completedDate || dayjs().format("YYYY-MM-DD")
              : "",
        });
      } catch {
        setTaskStatusOverrides((previous) => {
          if (!(task.id in previous)) {
            return previous;
          }

          const result: Record<string, MatterTaskType["status"]> = {};
          Object.entries(previous).forEach(([taskId, status]) => {
            if (taskId !== task.id) {
              result[taskId] = status;
            }
          });
          return result;
        });
      }
    },
    [massTortId, updateMatterTask],
  );

  useEffect(() => {
    setSelectedTaskIds((previous) => {
      if (!previous.size) {
        return previous;
      }

      const availableTaskIds = new Set(tasksWithType.map((task) => task.id));

      const next = new Set<string>();
      previous.forEach((taskId) => {
        if (availableTaskIds.has(taskId)) {
          next.add(taskId);
        }
      });

      return next.size === previous.size ? previous : next;
    });
  }, [tasksWithType]);

  // Clear selected tasks when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && selectedTaskIds.size > 0) {
        setSelectedTaskIds(new Set());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedTaskIds.size]);

  return (
    <>
      {!hasViewPermission && (
        <div className="flex min-h-0 flex-1 items-center justify-center p-4">
          <ActionEmptyState
            className="max-w-xl"
            icon={<LockKeyhole className="h-5 w-5" />}
            title="Tasks unavailable"
            description={`You do not have permission to view tasks for this ${massTortId ? "mass tort" : "matter"}.`}
          />
        </div>
      )}

      {hasViewPermission && (
        <div className="bg-muted/40 dark:bg-muted/20 flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-md border p-4">
          <div className="flex w-full flex-wrap items-center gap-2">
            <div className="flex h-10 w-full max-w-[350px] items-center">
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Search description, status, assigner, assignee"
              />
            </div>
            {showFilters ? (
              <TaskQuickFilters
                filterOptions={filterOptions}
                onDraftFiltersChange={setDraftTaskFilters}
                selectedFilters={selectedFilters}
                onAddFilter={handleAddFilter}
                onRemoveFilter={handleRemoveFilter}
                onClearFilters={() => {
                  setSelectedFilters({});
                }}
                sortOption={sortOption}
                onSortChange={setSortOption}
                showQuickFilters={showQuickFilters}
              />
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              {/* Create Task Button */}
              {hasCreatePermission && showCreateButton && (
                <div className="shrink-0">
                  <Button
                    type="button"
                    onClick={() => {
                      openTaskModal({
                        matterId: matter.id,
                        massTortId,
                        editing: false,
                        initialTab: "details",
                        subItem,
                        hideMatterField: true,
                      });
                    }}
                    data-testid="add-matter-task-button"
                  >
                    {shrinkCreateButton ? <Plus /> : "Create task"}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {massTortId && isMassTortTasksError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-2 text-sm"
            >
              <Text>Unable to load tasks.</Text>
              <Button
                variant="outline"
                disabled={isFetchingMassTortTasks}
                onClick={() => {
                  void (massTortTasksQuery.isFetchNextPageError
                    ? fetchMoreMassTortTasks()
                    : massTortTasksQuery.refetch());
                }}
              >
                Retry
              </Button>
            </div>
          )}
          {viewMode === "board" ? (
            isLoadingTasks ? (
              <div className="flex flex-1 items-center justify-center py-8">
                <LoadingSpinner className="h-6 w-6" />
              </div>
            ) : filteredTasks.length > 0 ? (
              <div className="mt-2 flex flex-1 overflow-hidden">
                <MatterTasksBoard
                  tasks={filteredTasks}
                  matterId={matter.id}
                  massTortId={massTortId}
                  subItem={subItem}
                  selectedTaskIds={selectedTaskIds}
                  selectedTasks={selectedTasks}
                  onToggleTaskSelection={handleToggleTaskSelection}
                  onTaskStatusChange={handleBoardStatusChange}
                  canEditStatus={
                    BOARD_DRAG_AND_DROP_ENABLED && hasUpdatePermission
                  }
                  getCustomBreadcrumbItems={getCustomBreadcrumbItems}
                />
              </div>
            ) : (
              <Text variant="subHeading" className="mt-2">
                No tasks found.
              </Text>
            )
          ) : (
            <AnimatePresence mode="wait">
              {isLoadingTasks ? (
                <div className="flex flex-1 items-center justify-center py-8">
                  <LoadingSpinner className="h-6 w-6" />
                </div>
              ) : (
                <motion.div
                  key="tasks-list"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    duration: showAnimation ? 0.3 : 0,
                    ease: "easeInOut",
                  }}
                  className="mt-2 flex flex-1 flex-col gap-2 overflow-auto pr-2"
                >
                  <Accordion
                    type="multiple"
                    defaultValue={["open", "in-progress"]}
                    className="space-y-2"
                  >
                    {taskGroups.map((group) => {
                      return (
                        <AccordionItem
                          key={group.value}
                          value={group.value}
                          className="bg-muted/40 dark:bg-muted/20 [&>h3]:bg-background px-3 [&>h3]:sticky [&>h3]:top-0 [&>h3]:z-10 [&>h3]:-mx-3"
                        >
                          <AccordionTrigger
                            iconPosition="left"
                            className="bg-muted justify-start gap-2 rounded-t-md border px-3 py-3 no-underline hover:no-underline"
                          >
                            <span className="flex items-center gap-2">
                              <TaskStatusIcon status={group.iconStatus} />
                              {group.label}
                              <span className="text-muted-foreground text-xs">
                                {group.totalCount}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="-mx-3 space-y-2 rounded-b-md border-x border-b px-3 pt-2">
                            {group.tasks.length > 0 ? (
                              group.tasks.map((task) => (
                                <MatterTask
                                  key={task.id}
                                  task={task}
                                  matter={matter}
                                  massTortId={massTortId}
                                  subItem={subItem}
                                  variant={viewMode}
                                  isSelected={selectedTaskIds.has(task.id)}
                                  onToggleSelect={handleToggleTaskSelection}
                                  selectedTasks={selectedTasks}
                                  customBreadcrumb={getCustomBreadcrumbItems?.(
                                    task,
                                  )}
                                />
                              ))
                            ) : (
                              <Text
                                variant="body"
                                className="text-muted-foreground py-2"
                              >
                                No tasks found.
                              </Text>
                            )}
                            {group.hasNextPage ? (
                              <div className="flex justify-center pt-3">
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={group.isFetchingNextPage}
                                  onClick={() => {
                                    void group.fetchNextPage();
                                  }}
                                >
                                  {group.isFetchingNextPage
                                    ? "Loading tasks..."
                                    : `Load more ${group.label.toLowerCase()} tasks`}
                                </Button>
                              </div>
                            ) : null}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          {massTortId && hasMoreMassTortTasks && !isMassTortTasksError && (
            <div className="flex shrink-0 justify-center pt-2">
              <Button
                variant="outline"
                disabled={isFetchingMassTortTasks}
                onClick={() => {
                  void fetchMoreMassTortTasks();
                }}
              >
                {isFetchingMassTortTasks
                  ? "Loading tasks..."
                  : "Load more tasks"}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
